// src/views/adminView/contractsView/CuentasUsuariosPanel.jsx
// Pestaña "Cuentas" del hub Empresas: administración GLOBAL de los usuarios de
// los comercios (antes vivían en /admin/users?tab=clientes; esa URL redirige
// acá). Lista todos los usuarios de empresas con contrato activo + los
// KAM/client del tenant, con filtro por empresa, búsqueda, estado y orden.
//
// Datos por useClientAccounts (carga compartida con la ficha de empresa y la
// cartera de Cumplimiento). Acciones: alta a una empresa existente
// (AgregarUsuarioEmpresaModal → createCompanyUser), editar
// (CreateClientBeartrack), bloquear/eliminar (BlockDeleteUserBeartrack),
// desbloquear (EnableUserModal) y marcas de vendedor/supervisor
// (updateUserApi) — estas últimas SOLO desde el detalle (UserDetail), no
// desde el menú de fila. Un comercio NUEVO se suma por "Sumar empresa →
// Como cliente" del header del hub (crea contrato + empresa + usuario).
//
// Detalle inline: reusa UserDetail con deep-link ?user= (el hub preserva los
// query params ajenos al cambiar de tab; adminContractsView limpia user/empresa
// al salir de Cuentas para que el detalle no reaparezca solo). El segmento del
// detalle manda por ROL: un delivery_commerce_fleet (flota propia del
// comercio) recibe detalle de chofer, no de comercio.
import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  Building2, Users as UsersIcon, Plus, Search, Eye, Pencil, Lock, Unlock,
  Trash2, MoreVertical, Check,
} from "lucide-react";
import { toast } from "../../../utils/toast";
import useAuth from "../../../hooks/useAuth";
import { useIsMobile } from "../../../hooks/useIsMobile";
import useUrlState from "../../../hooks/useUrlState";
import { updateUserApi } from "../../../api/user";
import { Stat } from "../../../components/ui/Stat";
import { Card } from "../../../components/ui/Card";
import { Badge } from "../../../components/ui/Badge";
import { Avatar } from "../../../components/ui/Avatar";
import { Button } from "../../../components/ui/Button";
import { EmptyState } from "../../../components/ui/EmptyState";
import { TableSkeleton } from "../../../components/ui/TableSkeleton";
import { Table, THead, TBody, TR, TH, TD } from "../../../components/ui/Table";
import { Select } from "../../../components/ui/Select";
import { SortControl } from "../../../components/ui/SortControl";
import Modal from "../../../components/modal";
import PackageTabs from "../../../components/packageTabs";
import CreateClientBeartrack from "../../../components/createClientBeartrack/createClientBeartrack";
import BlockDeleteUserBeartrack from "../../../components/blockDeleteUserBeartrack/blockDeleteUserBeartrack";
import EnableUserModal from "../../../components/enableUserModal/EnableUserModal";
import UserDetail from "../usersModule/UserDetail";
import {
  roleSegment, canBeSeller, roleTone, initialsOf, humanizeRole,
} from "../usersModule/usersConfig";
import { useClientAccounts, invalidateClientAccounts } from "./useClientAccounts";
import AgregarUsuarioEmpresaModal from "./AgregarUsuarioEmpresaModal";

export function CuentasUsuariosPanel({ contracts = [] }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const logisticCompanyId = user?.logisticCompany?._id || user?.logisticCompanyId || null;

  const { accounts, loading } = useClientAccounts();
  const list = useMemo(() => (Array.isArray(accounts) ? accounts : []), [accounts]);

  // ── Filtros ──
  // `empresa` va en la URL: es el deep-link que usa la ficha ("Administrar en
  // Cuentas") y hace la vista filtrada compartible. Búsqueda/estado/orden son
  // estado local (mismo criterio que los filtros client-side del hub).
  const [empresaFilter, setEmpresaFilter] = useUrlState("empresa", "");
  const [search, setSearch] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("");
  const [userSortField, setUserSortField] = useState(""); // "" | name | company | created
  const [userSortDir, setUserSortDir] = useState("desc");

  // Detalle inline (?user=), mismo patrón push que UsersModule: el back del
  // navegador vuelve a la lista.
  const [detailUserId, setDetailUserId] = useUrlState("user", "", { push: true });

  // ── Modales (mismo cableado que el ex tab clientes) ──
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [blockMode, setBlockMode] = useState(false);
  const [openClient, setOpenClient] = useState(false);
  const [openBlock, setOpenBlock] = useState(false);
  const [openAdd, setOpenAdd] = useState(false); // alta a empresa existente
  const [enableMode, setEnableMode] = useState(null); // "unblock" | null
  const [rowMenu, setRowMenu] = useState(null);
  const [rowMenuPos, setRowMenuPos] = useState(null);

  // RUT (businessIdentifier) por empresa desde los contratos del hub: el
  // populate de users/clients trae solo {_id, name} y la card "Comercio" del
  // detalle lo mostraría vacío. Best-effort: la cartera puede estar cargada
  // parcialmente.
  const identifierByCompany = useMemo(() => {
    const m = new Map();
    (contracts || []).forEach((c) => {
      const id = c?.companyId?._id;
      if (id && c.companyId.businessIdentifier) m.set(id, c.companyId.businessIdentifier);
    });
    return m;
  }, [contracts]);

  // ── Empresas presentes (para el select y el KPI) ──
  const companies = useMemo(() => {
    const m = new Map();
    list.forEach((u) => {
      const c = u?.companyId;
      if (c?._id && !m.has(c._id)) m.set(c._id, c.name || c._id);
    });
    return [...m.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [list]);

  // Empresas elegibles para el alta: unión de los contratos ACTIVOS del hub y
  // las empresas ya presentes en la lista (los contratos pueden venir
  // paginados; la unión cubre el resto).
  const selectableCompanies = useMemo(() => {
    const m = new Map();
    (contracts || []).forEach((c) => {
      const id = c?.companyId?._id;
      if (c?.status === "active" && id && !m.has(id)) m.set(id, c.companyId.name || id);
    });
    companies.forEach((c) => { if (!m.has(c.id)) m.set(c.id, c.name); });
    return [...m.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [contracts, companies]);

  // ── Lista visible ──
  const visible = useMemo(() => {
    let out = list;
    if (empresaFilter) out = out.filter((u) => u?.companyId?._id === empresaFilter);
    if (estadoFilter === "active") out = out.filter((u) => u.active !== false);
    if (estadoFilter === "blocked") out = out.filter((u) => u.active === false);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((u) =>
        `${u.name || ""} ${u.lastName || ""} ${u.email || ""} ${u?.companyId?.name || ""} ${u.rut || ""} ${u?.dniData?.dniNumber || ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    if (userSortField) {
      const asc = userSortDir === "asc";
      const name = (u) => `${u.name || ""} ${u.lastName || ""}`.trim().toLowerCase();
      const companyName = (u) => (u?.companyId?.name || "").toLowerCase();
      const created = (u) => (u.createdAt ? new Date(u.createdAt).getTime() : 0);
      const cmp = {
        name: (a, b) => (asc ? name(a).localeCompare(name(b)) : name(b).localeCompare(name(a))),
        // Desempate por nombre siempre ASC: dentro de un mismo comercio la
        // lista queda legible en ambos sentidos.
        company: (a, b) =>
          (asc
            ? companyName(a).localeCompare(companyName(b))
            : companyName(b).localeCompare(companyName(a))) || name(a).localeCompare(name(b)),
        created: (a, b) => (asc ? created(a) - created(b) : created(b) - created(a)),
      }[userSortField];
      if (cmp) out = [...out].sort(cmp);
    }
    return out;
  }, [list, empresaFilter, estadoFilter, search, userSortField, userSortDir]);

  // ── Detalle inline ──
  const detailUser = useMemo(
    () => (detailUserId ? list.find((x) => x._id === detailUserId) || null : null),
    [detailUserId, list],
  );
  // El ROL manda: un delivery_commerce_fleet es chofer (heatmap por driverId,
  // vehículo, documentos), aunque venga en users/clients.
  const detailSeg = detailUser && roleSegment(detailUser?.role?.name) === "choferes"
    ? "choferes"
    : "clientes";
  // RUT de la empresa inyectado al vuelo para la card "Comercio" del detalle.
  const detailUserEnriched = useMemo(() => {
    if (!detailUser?.companyId?._id) return detailUser;
    const businessIdentifier =
      detailUser.companyId.businessIdentifier || identifierByCompany.get(detailUser.companyId._id);
    return businessIdentifier
      ? { ...detailUser, companyId: { ...detailUser.companyId, businessIdentifier } }
      : detailUser;
  }, [detailUser, identifierByCompany]);

  // ?user= muerto (usuario eliminado / id inválido): limpiar el param para no
  // arrastrarlo en cada navegación posterior.
  useEffect(() => {
    if (detailUserId && !loading && Array.isArray(accounts) && !detailUser) setDetailUserId("");
  }, [detailUserId, loading, accounts, detailUser, setDetailUserId]);

  // ── Menú de fila (portaleado, mismo criterio que UsersModule) ──
  const openRowMenu = (e, id) => {
    e.stopPropagation();
    if (rowMenu === id) { setRowMenu(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const MENU_W = 208;
    const MENU_H = 300;
    const left = Math.max(8, r.right - MENU_W);
    const openUp = r.bottom + MENU_H > window.innerHeight && r.top > MENU_H;
    setRowMenuPos(openUp
      ? { left, bottom: window.innerHeight - r.top + 4 }
      : { left, top: r.bottom + 4 });
    setRowMenu(id);
  };
  useEffect(() => {
    const onClick = (e) => {
      if (rowMenu && !e.target.closest(".ca-rowmenu")) setRowMenu(null);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [rowMenu]);
  useEffect(() => {
    if (!rowMenu) return undefined;
    const close = () => setRowMenu(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [rowMenu]);

  // ── Acciones (mismos handlers/APIs que el ex tab clientes) ──
  const refetch = useCallback(() => invalidateClientAccounts(), []);
  const closeAll = () => { setOpenBlock(false); setEditMode(false); setBlockMode(false); setSelectedUser(null); };
  const onView = (u) => { setRowMenu(null); setDetailUserId(u._id); };
  const onEdit = (u) => { setSelectedUser(u); setEditMode(true); setRowMenu(null); setOpenClient(true); };
  const onBlock = (u) => { setSelectedUser(u); setBlockMode(true); setRowMenu(null); setOpenBlock(true); };
  const onDelete = (u) => { setSelectedUser(u); setBlockMode(false); setRowMenu(null); setOpenBlock(true); };
  const onEnable = (u) => { setSelectedUser(u); setRowMenu(null); setEnableMode("unblock"); };

  // Marca/desmarca VENDEDOR (`isSeller`) y SUPERVISOR DE VENTAS
  // (`isSalesSupervisor`) — solo expuestos dentro del detalle (UserDetail),
  // no en el menú de fila. Gate por ROL (canBeSeller), no por la lista: acá
  // también hay repartidores de flota propia con bankData personal. Tras el
  // PATCH se invalida la caché compartida: refetch de fondo sin skeleton.
  const onToggleSeller = async (u) => {
    const next = !u.isSeller;
    const { error } = await updateUserApi({ userId: u._id, values: { isSeller: next } });
    if (error) {
      toast.error(error?.message || t("No pudimos actualizar la marca de vendedor"));
      return;
    }
    refetch();
    toast.success(next ? t("Usuario marcado como vendedor") : t("Se quitó la marca de vendedor"));
  };
  const onToggleSalesSupervisor = async (u) => {
    const next = !u.isSalesSupervisor;
    const { error } = await updateUserApi({ userId: u._id, values: { isSalesSupervisor: next } });
    if (error) {
      toast.error(error?.message || t("No pudimos actualizar la marca de supervisor"));
      return;
    }
    refetch();
    toast.success(
      next
        ? t("Usuario marcado como supervisor de ventas")
        : t("Se quitó la marca de supervisor de ventas"),
    );
  };

  const RowMenuItems = ({ u }) => {
    const blocked = u.active === false;
    const itemCls = "w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-fg hover:bg-bg-muted disabled:opacity-50";
    return (
      <>
        <button type="button" className={itemCls} onClick={() => onView(u)}><Eye className="w-4 h-4" />{t("Ver")}</button>
        <button type="button" className={itemCls} onClick={() => onEdit(u)}><Pencil className="w-4 h-4" />{t("Editar")}</button>
        {blocked ? (
          <button type="button" className={itemCls} onClick={() => onEnable(u)}>
            <Unlock className="w-4 h-4" />{t("Desbloquear")}
          </button>
        ) : (
          <button type="button" className={itemCls} onClick={() => onBlock(u)}>
            <Lock className="w-4 h-4" />{t("Bloquear")}
          </button>
        )}
        <button type="button" className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-status-error hover:bg-status-error-soft" onClick={() => onDelete(u)}><Trash2 className="w-4 h-4" />{t("Eliminar")}</button>
      </>
    );
  };

  const RowMenuPortal = ({ u }) => {
    if (!rowMenuPos) return null;
    return createPortal(
      <div
        className="ca-rowmenu fixed z-50 w-52 rounded-lg border border-border bg-bg shadow-lg p-1"
        style={rowMenuPos}
        onClick={(e) => e.stopPropagation()}
      >
        <RowMenuItems u={u} />
      </div>,
      document.body
    );
  };

  const StatusBadge = ({ u }) => {
    const blocked = u.active === false;
    return <Badge tone={blocked ? "neutral" : "success"} dot>{blocked ? t("Bloqueado") : t("Activo")}</Badge>;
  };

  // CreateClientBeartrack exige AMBOS callbacks de cierre (su Cancelar llama
  // cerrarModal sin guard); fetch= se dispara tras guardar.
  const tabContentClient = [<CreateClientBeartrack key="c" closeModal={setOpenClient} cerrarModal={closeAll} fetch={refetch} user={selectedUser} edit={editMode} />];
  const tabContentBlock = [<BlockDeleteUserBeartrack key="bd" closeModal={setOpenBlock} cerrarModal={closeAll} fetch={refetch} user={selectedUser} block={blockMode} />];

  const activos = useMemo(() => list.filter((u) => u.active !== false).length, [list]);
  // Fetch fallido: los KPIs no muestran un 0 falso y la tabla muestra el
  // estado de error (no el "sin usuarios" de una lista vacía real).
  const failed = accounts === null;

  if (detailUser) {
    return (
      <>
        <UserDetail
          user={detailUserEnriched}
          seg={detailSeg}
          isPendingDriver={false}
          canManageTeam={false}
          busyId={null}
          onBack={() => setDetailUserId("")}
          onEdit={onEdit}
          onBlock={onBlock}
          onDelete={onDelete}
          onEnable={onEnable}
          onToggleSeller={onToggleSeller}
          onToggleSalesSupervisor={onToggleSalesSupervisor}
        />
        <Modal size="md" open={openBlock} setOpen={() => { setOpenBlock(false); setBlockMode(false); setSelectedUser(null); }} body={<PackageTabs tabContent={tabContentBlock} />} />
        <Modal size="md" open={openClient} setOpen={() => { setOpenClient(false); if (!editMode) setSelectedUser(null); }} body={<PackageTabs tabContent={tabContentClient} />} />
        <EnableUserModal
          open={!!enableMode}
          mode="unblock"
          user={selectedUser}
          logisticCompanyId={logisticCompanyId}
          onClose={() => { setEnableMode(null); setSelectedUser(null); }}
          onDone={refetch}
        />
      </>
    );
  }

  if (detailUserId && loading) {
    // Deep-link ?user= mientras carga: skeleton con la forma del detalle.
    return (
      <div className="space-y-4" aria-hidden="true">
        <div className="h-32 rounded-lg bg-bg-subtle animate-pulse" />
        <div className="h-44 rounded-lg bg-bg-subtle animate-pulse" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="h-48 rounded-lg bg-bg-subtle animate-pulse" />
          <div className="h-48 rounded-lg bg-bg-subtle animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid max-w-6xl grid-cols-2 sm:grid-cols-3 gap-3">
        <Stat
          label={t("Usuarios de comercios")}
          value={loading || failed ? "—" : list.length}
          icon={<UsersIcon />}
          tone="brand"
        />
        <Stat
          label={t("Activos")}
          value={loading || failed ? "—" : activos}
          icon={<Check />}
          tone="success"
          hint={loading || failed ? null : t("de {{n}} en el segmento", { n: list.length })}
        />
        <Stat
          label={t("Comercios distintos")}
          value={loading || failed ? "—" : companies.length}
          icon={<Building2 />}
          tone="info"
        />
      </div>

      <Card padding="none" className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-fg">{t("Usuarios de los comercios")}</h2>
            <p className="text-xs text-fg-muted">{t("Cuentas con acceso al portal de cada comercio")}</p>
          </div>
          {/* Alta EN el panel a una empresa EXISTENTE (createCompanyUser, el
              mismo flujo del usuario inicial del wizard de contratos). Para un
              comercio nuevo está "Sumar empresa → Como cliente" en el header
              del hub, que además crea el contrato. */}
          <Button
            onClick={() => setOpenAdd(true)}
            iconLeft={<Plus className="w-4 h-4" />}
          >
            {t("Agregar usuario")}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-3">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("Buscar por nombre, email, RUT o comercio…")}
              aria-label={t("Buscar usuarios")}
              className="h-9 w-full rounded-lg border border-border bg-bg pl-9 pr-3 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <Select
            aria-label={t("Filtrar por empresa")}
            value={empresaFilter}
            onChange={(e) => setEmpresaFilter(e.target.value)}
            className="h-9 w-56"
            options={[
              { value: "", label: t("Todas las empresas") },
              ...companies.map((c) => ({ value: c.id, label: c.name })),
              // Un ?empresa= (llegado desde la ficha) cuya empresa no tiene
              // usuarios no está entre las options de arriba: sin esta entrada
              // el Select mostraría "Todas las empresas" con el filtro
              // igualmente aplicado (filtro invisible). El nombre sale de los
              // contratos del hub si están cargados.
              ...(empresaFilter && !companies.some((c) => c.id === empresaFilter)
                ? [{
                    value: empresaFilter,
                    label:
                      (contracts || []).find((c) => c?.companyId?._id === empresaFilter)
                        ?.companyId?.name || t("Empresa sin usuarios"),
                  }]
                : []),
            ]}
          />
          <Select
            aria-label={t("Filtrar por estado")}
            value={estadoFilter}
            onChange={(e) => setEstadoFilter(e.target.value)}
            className="h-9 w-48"
            options={[
              { value: "", label: t("Todos los estados") },
              { value: "active", label: t("Activos") },
              { value: "blocked", label: t("Bloqueados") },
            ]}
          />
          <SortControl
            ariaLabel={t("Ordenar usuarios")}
            field={userSortField}
            dir={userSortDir}
            onFieldChange={setUserSortField}
            onDirChange={setUserSortDir}
            selectClassName="h-9 w-56"
            buttonClassName="h-9 w-9"
            fields={[
              { value: "name", label: t("Por nombre"), defaultDir: "asc" },
              { value: "company", label: t("Por comercio"), defaultDir: "asc" },
              { value: "created", label: t("Por fecha de registro"), defaultDir: "desc" },
            ]}
          />
        </div>

        {loading ? (
          <TableSkeleton rows={8} columns={6} className="border-0 rounded-none" />
        ) : failed ? (
          <EmptyState
            icon={<UsersIcon />}
            title={t("No pudimos cargar las cuentas")}
            description={t("Reintenta en unos minutos.")}
          />
        ) : visible.length === 0 ? (
          <EmptyState
            icon={<UsersIcon />}
            title={t("Sin usuarios")}
            description={search || empresaFilter || estadoFilter ? t("Nada coincide con el filtro.") : t("No hay usuarios de comercios todavía.")}
          />
        ) : isMobile ? (
          <div className="p-3 space-y-3">
            {visible.map((u) => {
              const blocked = u.active === false;
              return (
                <div key={u._id} className="rounded-xl border border-border bg-bg p-4 space-y-3 cursor-pointer"
                  onClick={() => onView(u)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar initials={initialsOf(u.name, u.lastName)} size="sm" tone={blocked ? "neutral" : "brand"} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-fg truncate">{u.name} {u.lastName}</div>
                        <div className="text-xs text-fg-muted truncate">{u.email || "—"}</div>
                      </div>
                    </div>
                    <div className="ca-rowmenu relative shrink-0">
                      <button type="button" aria-label={t("Acciones")} className="p-1.5 rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg"
                        onClick={(e) => openRowMenu(e, u._id)}>
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {rowMenu === u._id && <RowMenuPortal u={u} />}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={roleTone(u?.role?.name)}>{humanizeRole(u?.role?.name_public || u?.role?.name || "—")}</Badge>
                    <StatusBadge u={u} />
                    {u.isSeller && canBeSeller(u) && <Badge tone="info">{t("Vendedor")}</Badge>}
                    {u.isSalesSupervisor && canBeSeller(u) && <Badge tone="warning">{t("Supervisor de ventas")}</Badge>}
                    <span className="text-xs text-fg-muted ml-auto truncate">{u?.companyId?.name || "—"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <tr>
                  <TH>{t("Usuario")}</TH>
                  <TH>{t("Tipo")}</TH>
                  <TH>{t("RUT / Teléfono")}</TH>
                  <TH>{t("Comercio")}</TH>
                  <TH>{t("Estado")}</TH>
                  <TH className="w-10"></TH>
                </tr>
              </THead>
              <TBody>
                {visible.map((u) => {
                  const blocked = u.active === false;
                  const rut = u.rut || u?.dniData?.dniNumber || null;
                  return (
                    <TR key={u._id} className="cursor-pointer" onClick={() => onView(u)}>
                      <TD>
                        <div className="flex items-center gap-2.5">
                          <Avatar initials={initialsOf(u.name, u.lastName)} size="sm" tone={blocked ? "neutral" : "brand"} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-medium text-fg truncate">{u.name} {u.lastName}</span>
                              {u.isSeller && canBeSeller(u) && <Badge tone="info">{t("Vendedor")}</Badge>}
                              {u.isSalesSupervisor && canBeSeller(u) && <Badge tone="warning">{t("Supervisor de ventas")}</Badge>}
                            </div>
                            <div className="text-xs text-fg-muted truncate">{u.email || "—"}</div>
                          </div>
                        </div>
                      </TD>
                      <TD>
                        <Badge tone={roleTone(u?.role?.name)}>
                          {humanizeRole(u?.role?.name_public || u?.role?.name || "—")}
                        </Badge>
                      </TD>
                      <TD>
                        {rut ? (
                          <span className="text-sm font-mono text-fg-muted whitespace-nowrap">{rut}</span>
                        ) : (
                          <span className="text-sm text-fg-muted whitespace-nowrap">{u.phone || "—"}</span>
                        )}
                      </TD>
                      <TD><span className="text-sm text-fg-muted">{u?.companyId?.name || "—"}</span></TD>
                      <TD><StatusBadge u={u} /></TD>
                      <TD>
                        <div className="ca-rowmenu relative flex justify-end">
                          <button type="button" aria-label={t("Acciones")} className="p-1.5 rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg"
                            onClick={(e) => openRowMenu(e, u._id)}>
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {rowMenu === u._id && <RowMenuPortal u={u} />}
                        </div>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </div>
        )}
      </Card>

      {/* ── Modales (los mismos del ex tab clientes) ── */}
      <Modal size="md" open={openBlock} setOpen={() => { setOpenBlock(false); setBlockMode(false); setSelectedUser(null); }} body={<PackageTabs tabContent={tabContentBlock} />} />
      <Modal size="md" open={openClient} setOpen={() => { setOpenClient(false); if (!editMode) setSelectedUser(null); }} body={<PackageTabs tabContent={tabContentClient} />} />
      <EnableUserModal
        open={!!enableMode}
        mode="unblock"
        user={selectedUser}
        logisticCompanyId={logisticCompanyId}
        onClose={() => { setEnableMode(null); setSelectedUser(null); }}
        onDone={refetch}
      />
      <AgregarUsuarioEmpresaModal
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        companies={selectableCompanies}
        defaultCompanyId={empresaFilter}
        onDone={refetch}
      />
    </div>
  );
}

export default CuentasUsuariosPanel;
